/**
 * AutocompleteInput — a free-text input with a filtered suggestion list.
 *
 * A lightweight combobox-lite: the user can type anything (the value is
 * never constrained to the suggestion set), but a dropdown surfaces the
 * suggestions whose label contains the current text. Picking one fills the
 * field. Introduced for the Add-a-Thing dialog (P6.3) so tag / unit names
 * stay consistent with what already exists in the org, but kept generic.
 *
 * Mouse + keyboard: ↑/↓ move the highlight, Enter accepts it, Esc closes.
 */

import {
  useId,
  useMemo,
  useRef,
  useState,
  type InputHTMLAttributes,
} from "react";
import { Input } from "@/lib/ui/input";
import { cn } from "@/lib/ui/cn";

export interface AutocompleteInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange"> {
  value: string;
  onChange: (next: string) => void;
  /** Candidate values to suggest from (deduped + filtered as you type). */
  suggestions: string[];
  /** Cap the visible suggestions (default 8). */
  max?: number;
}

export function AutocompleteInput({
  value,
  onChange,
  suggestions,
  max = 8,
  className,
  onFocus,
  onBlur,
  onKeyDown,
  ...rest
}: AutocompleteInputProps) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const listId = useId();
  // Close on blur, but only after a click on an option has had a chance to
  // register (mousedown fires before blur, so we suppress the close there).
  const suppressBlur = useRef(false);

  const matches = useMemo(() => {
    const needle = value.trim().toLowerCase();
    const seen = new Set<string>();
    const out: string[] = [];
    for (const s of suggestions) {
      const v = s.trim();
      if (!v) continue;
      const key = v.toLowerCase();
      if (seen.has(key)) continue;
      if (key === needle) continue; // already typed exactly — nothing to add
      if (needle && !key.includes(needle)) continue;
      seen.add(key);
      out.push(v);
      if (out.length >= max) break;
    }
    return out;
  }, [suggestions, value, max]);

  const showList = open && matches.length > 0;

  const accept = (v: string) => {
    onChange(v);
    setOpen(false);
    setActive(-1);
  };

  return (
    <div className="relative">
      <Input
        {...rest}
        className={className}
        value={value}
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        autoComplete="off"
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setActive(-1);
        }}
        onFocus={(e) => {
          setOpen(true);
          onFocus?.(e);
        }}
        onBlur={(e) => {
          if (suppressBlur.current) {
            suppressBlur.current = false;
            return;
          }
          setOpen(false);
          setActive(-1);
          onBlur?.(e);
        }}
        onKeyDown={(e) => {
          onKeyDown?.(e);
          if (!showList) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((i) => (i + 1) % matches.length);
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((i) => (i <= 0 ? matches.length - 1 : i - 1));
          } else if (e.key === "Enter" && active >= 0) {
            e.preventDefault();
            accept(matches[active]);
          } else if (e.key === "Escape") {
            setOpen(false);
            setActive(-1);
          }
        }}
      />
      {showList && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-md border border-border bg-surface py-1 shadow-lg"
        >
          {matches.map((m, i) => (
            <li key={m} role="option" aria-selected={i === active}>
              <button
                type="button"
                // mousedown (not click) so it fires before the input blur.
                onMouseDown={() => {
                  suppressBlur.current = true;
                }}
                onClick={() => accept(m)}
                onMouseEnter={() => setActive(i)}
                className={cn(
                  "flex w-full items-center px-3 py-1.5 text-left text-sm",
                  i === active
                    ? "bg-brand-muted text-brand"
                    : "text-on-surface hover:bg-surface-muted",
                )}
              >
                {m}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
